import { EventCategories, type EventCategory, EventCategoryIconMap, EventCategoryMap } from "@qendo/calendar";
import {
    type ProfileRole,
    ProfileRoleIconMap,
    ProfileRoleMap,
    ProfileRoles,
    SelectableProfileRoleMap,
} from "@qendo/profile";
import type { OptionData, OptionGroup, OptionType } from "@qendo/types";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import {
    ComboBox,
    type ComboBoxProps,
    type GroupComboProps,
    type ItemType,
} from "~/components/form/combo-box/ComboBox";
import { QendoIcon } from "~/components/QendoIcon";
import { useOptions } from "~/providers/OptionsProvider";
import { filled } from "~/util";

type OptionSelectorProps<T extends OptionType> = {
    readonly type: T;
    value: string | null;
    onItemSelected: ((v: string | null) => void) | Dispatch<SetStateAction<string | null>>;
    addable?: never;
    onAddItem?: never;
    exclude?: string[] | null;
} & Omit<ComboBoxProps<string, OptionData>, "items" | "addable" | "onAddItem" | "mapItems">;

function isProfileRole(group?: OptionGroup | null): group is ProfileRole {
    return group ? ProfileRoles.includes(group as ProfileRole) : false;
}
function isEventCategory(group?: OptionGroup | null): group is EventCategory {
    return group ? EventCategories.includes(group as EventCategory) : false;
}

type GroupCombo<TGroup extends OptionGroup> = GroupComboProps<TGroup, TGroup, ItemType<TGroup>>;

const mapOptions = (option: OptionData) => ({
    value: option.id,
    label: option.title,
    icon: () => (
        <>
            {filled(option.colour) && <QendoIcon name="circle-md-filled" size={16} color={option.colour} />}
            {isProfileRole(option.group) && (
                <QendoIcon name={ProfileRoleIconMap[option.group]} size={16} variant="secondary" />
            )}
            {isEventCategory(option.group) && (
                <QendoIcon name={EventCategoryIconMap[option.group]} size={16} variant="secondary" />
            )}
        </>
    ),
    subtitle:
        (isProfileRole(option.group) && ProfileRoleMap[option.group]) ||
        (isEventCategory(option.group) && EventCategoryMap[option.group]) ||
        undefined,
});

export function OptionSelector<T extends OptionType = OptionType>({
    type,
    exclude,
    ...comboProps
}: OptionSelectorProps<T>) {
    const { canMakeOption, storeOption, listOptions } = useOptions();

    const items = useMemo(
        () => listOptions(type).filter((option) => !exclude?.includes(option.id)),
        [listOptions, type, exclude],
    );
    const groups = useMemo(
        () =>
            (items.some((v) => isProfileRole(v.group)) &&
                ({
                    items: Object.keys(SelectableProfileRoleMap),
                    label: "Type",
                    placeholder: "Select role type",
                    name: "group",
                    mapItems: (value: ProfileRole) => ({
                        value,
                        label: ProfileRoleMap[value],
                        icon: () => <QendoIcon name={ProfileRoleIconMap[value]} size={16} variant="secondary" />,
                    }),
                } as GroupCombo<ProfileRole>)) ||
            (items.some((v) => isEventCategory(v.group)) &&
                ({
                    items: Object.keys(EventCategoryMap),
                    label: "Event type",
                    placeholder: "Select event type",
                    name: "group",
                    mapItems: (value: EventCategory) => ({
                        value,
                        label: EventCategoryMap[value],
                        icon: () => <QendoIcon name={EventCategoryIconMap[value]} size={16} variant="secondary" />,
                    }),
                } as GroupCombo<EventCategory>)) ||
            undefined,
        [items],
    );

    const addable = canMakeOption(type);
    const onAddItem = async (value: string, group?: string | null) => {
        if (!addable) {
            return null;
        }

        return await storeOption({
            type,
            title: value,
            group: group as OptionGroup,
        });
    };

    return (
        <ComboBox<string, OptionData>
            {...comboProps}
            items={items}
            addable={addable}
            onAddItem={onAddItem}
            mapItems={mapOptions}
            groups={groups as GroupComboProps}
        />
    );
}
